/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';
import type { Interpolation } from '@emotion/react';

import * as React from 'react';

import config from 'js/app/config';

import { Button, Typography, Typography2, breakpoints } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import { TrackedA } from 'bundles/page/components/TrackedLink2';

export const PROMOTIONS = {
  TEAMS_PROMOTION: 'TEAMS_PROMOTION',
} as const;

type Props = {
  promotion: keyof typeof PROMOTIONS | undefined | null;
};

function TeamsPromotion() {
  const styles: Record<string, Interpolation<Theme>> = {
    container: () => css`
      width: 428px;
      max-width: 100%;
      background: var(--cds-color-grey-25);
      padding: var(--cds-spacing-600) var(--cds-spacing-300) var(--cds-spacing-600) var(--cds-spacing-300);

      ${breakpoints.down('sm')} {
        padding: var(--cds-spacing-600) var(--cds-spacing-200) var(--cds-spacing-600) var(--cds-spacing-200);
        margin-bottom: var(--cds-spacing-600);
        margin-top: var(--cds-spacing-600);
      }
    `,
    eyebrow: () => css`
      text-transform: uppercase;
      margin-bottom: var(--cds-spacing-300);

      ${breakpoints.down('sm')} {
        font-size: 14px;
      }
    `,
    title: () => css`
      margin-bottom: var(--cds-spacing-300);
      font-size: 40px !important;
      line-height: 1 !important;
      letter-spacing: -1px;

      ${breakpoints.down('sm')} {
        font-size: 30px !important;
      }
    `,
    description: () => css`
      margin-bottom: var(--cds-spacing-400);

      ${breakpoints.down('sm')} {
        margin-bottom: var(--cds-spacing-300);
        font-size: 14px;
      }
    `,
    button: () => css`
      margin-bottom: var(--cds-spacing-300);

      ${breakpoints.down('sm')} {
        font-size: 14px;
      }
    `,
    price: () => css`
      ${breakpoints.down('sm')} {
        font-size: 14px;
      }
    `,
    priceBold: () => css`
      font-weight: bold;
    `,
  };

  const track = useTracker();

  const handleClick = () => {
    track('click_button', {
      button: {
        name: 'ent_website_teams_get_started',
      },
    });
  };

  return (
    <div css={styles.container}>
      <Typography variant="h2semibold" css={styles.eyebrow}>
        Do you have 5-125 users?
      </Typography>
      <Typography variant="d2semibold" css={styles.title}>
        Upskill your team with world-class learning
      </Typography>
      <Typography2 component="p" variant="bodyPrimary" css={styles.description}>
        Provide your team unlimited access to 5,200+ courses, Specializations, and Guided Projects, for one subscription
        price.
      </Typography2>
      <Button
        onClick={handleClick}
        variant="primary"
        component={TrackedA}
        css={styles.button}
        href={`${config.url.base}business/teams`}
        trackingName="navigation_c4b_promotion"
      >
        Get started
      </Button>
      <Typography2 component="p" variant="bodyPrimary" css={styles.price}>
        <br />
        14-day refund guarantee
      </Typography2>
    </div>
  );
}

function PromotionWrapper({ promotion }: Props) {
  switch (promotion) {
    case PROMOTIONS.TEAMS_PROMOTION:
      return <TeamsPromotion />;
    default:
      return null;
  }
}

export default PromotionWrapper;
